/**
 * Institution Status Enum
 *
 * Values synced with backend InstitutionStatusEnum.
 * @see backend/src/backend/db/constants/institution_status.py
 *
 * Multi-step approval flow:
 * 1. PENDING_APPROVAL_GESTAO_LENA (5) - First step: Gestão Lena approval
 * 2. PENDING_APPROVAL_REGULATORIO (6) - Second step: Regulatório approval
 * 3. PENDING_APPROVAL_DIRETORIA (7) - Third step: Diretoria approval (only for Buyer category)
 * 4. APPROVED (2) - Fully approved
 *
 * Legacy statuses:
 * - PENDING_APPROVAL (1) - Old generic pending status
 * - REJECTED (3) - Rejected at any step
 * - INACTIVE (4) - Deactivated institution
 */
import { intl } from "dmsbasereact";

export const InstitutionStatusEnum = {
    PENDING_APPROVAL: 1,
    APPROVED: 2,
    REJECTED: 3,
    INACTIVE: 4,
    PENDING_APPROVAL_GESTAO_LENA: 5,
    PENDING_APPROVAL_REGULATORIO: 6,
    PENDING_APPROVAL_DIRETORIA: 7,
    PENDING_DOCUMENT_UPLOAD: 8,
};

export const INSTITUTION_STATUSES = [
    { value: InstitutionStatusEnum.PENDING_APPROVAL, labelKey: 'status.pending_approval', defaultLabel: 'Pending Approval' },
    { value: InstitutionStatusEnum.APPROVED, labelKey: 'status.active', defaultLabel: 'Active' },
    { value: InstitutionStatusEnum.REJECTED, labelKey: 'status.rejected', defaultLabel: 'Rejected' },
    { value: InstitutionStatusEnum.INACTIVE, labelKey: 'status.inactive', defaultLabel: 'Inactive' },
    { value: InstitutionStatusEnum.PENDING_APPROVAL_GESTAO_LENA, labelKey: 'status.pending_gestao_lena', defaultLabel: 'Pending - Gestão Lena' },
    { value: InstitutionStatusEnum.PENDING_APPROVAL_REGULATORIO, labelKey: 'status.pending_regulatorio', defaultLabel: 'Pending - Regulatório' },
    { value: InstitutionStatusEnum.PENDING_APPROVAL_DIRETORIA, labelKey: 'status.pending_diretoria', defaultLabel: 'Pending - Diretoria' },
];

export const InstitutionStatus = {
    getText: (code) => {
        switch (code) {
            case InstitutionStatusEnum.PENDING_APPROVAL:
                return intl.get("PendingApproval").d("Pending Approval");
            case InstitutionStatusEnum.APPROVED:
                return intl.get("Active").d("Active");
            case InstitutionStatusEnum.REJECTED:
                return intl.get("Rejected").d("Rejected");
            case InstitutionStatusEnum.INACTIVE:
                return intl.get("Inactive").d("Inactive");
            case InstitutionStatusEnum.PENDING_APPROVAL_GESTAO_LENA:
                return intl.get("PendingGestaoLena").d("Pending - Gestão Lena");
            case InstitutionStatusEnum.PENDING_APPROVAL_REGULATORIO:
                return intl.get("PendingRegulatorio").d("Pending - Regulatório");
            case InstitutionStatusEnum.PENDING_APPROVAL_DIRETORIA:
                return intl.get("PendingDiretoria").d("Pending - Diretoria");
            case InstitutionStatusEnum.PENDING_DOCUMENT_UPLOAD:
                return intl.get("PendingDocumentUpload").d("Awaiting Documents");
            default:
                return intl.get("Unknown").d("Unknown");
        }
    },
    getColor: (code) => {
        switch (code) {
            case InstitutionStatusEnum.PENDING_APPROVAL:
            case InstitutionStatusEnum.PENDING_APPROVAL_GESTAO_LENA:
            case InstitutionStatusEnum.PENDING_APPROVAL_REGULATORIO:
            case InstitutionStatusEnum.PENDING_APPROVAL_DIRETORIA:
                return "warning";
            case InstitutionStatusEnum.PENDING_DOCUMENT_UPLOAD:
                return "info";
            case InstitutionStatusEnum.APPROVED:
                return "success";
            case InstitutionStatusEnum.REJECTED:
                return "error";
            case InstitutionStatusEnum.INACTIVE:
                return "default";
            default:
                return "default";
        }
    }
};

/**
 * Categories that require director approval (Buyer only)
 */
export const CATEGORIES_REQUIRING_DIRECTOR_APPROVAL = [104];

/**
 * Categories that only need Regulatório approval (Dispenser, Prescriber, Distributor, Intermediary)
 */
export const CATEGORIES_FINAL_AT_REGULATORIO = [100, 101, 102, 103];

/**
 * Get institution status options for select components
 * @returns {Array} Array of {value, label} objects
 */
export const getInstitutionStatusOptions = () => [
    { value: InstitutionStatusEnum.PENDING_APPROVAL, label: InstitutionStatus.getText(InstitutionStatusEnum.PENDING_APPROVAL) },
    { value: InstitutionStatusEnum.APPROVED, label: InstitutionStatus.getText(InstitutionStatusEnum.APPROVED) },
    { value: InstitutionStatusEnum.REJECTED, label: InstitutionStatus.getText(InstitutionStatusEnum.REJECTED) },
    { value: InstitutionStatusEnum.INACTIVE, label: InstitutionStatus.getText(InstitutionStatusEnum.INACTIVE) },
    { value: InstitutionStatusEnum.PENDING_APPROVAL_GESTAO_LENA, label: InstitutionStatus.getText(InstitutionStatusEnum.PENDING_APPROVAL_GESTAO_LENA) },
    { value: InstitutionStatusEnum.PENDING_APPROVAL_REGULATORIO, label: InstitutionStatus.getText(InstitutionStatusEnum.PENDING_APPROVAL_REGULATORIO) },
    { value: InstitutionStatusEnum.PENDING_APPROVAL_DIRETORIA, label: InstitutionStatus.getText(InstitutionStatusEnum.PENDING_APPROVAL_DIRETORIA) },
];
